'use client';
import { OnbForm } from '@/components/form';
import { sendOnbEvent } from '@/components/newRelic';
import { resetErrors } from '@/redux/errorSlice';
import { connectedField } from '@/redux/fields';
import { showToast } from '@/redux/navigationSlice';
import { RootState } from '@/redux/store';
import { Button, Stack, Typography } from '@mui/material';
import { emailVerification, initializeStep } from '@repo/onb-api';
import { Otp } from '@repo/ui';
import { failureDetails, getClientCookie } from '@repo/utils';
import { useRouter } from 'next/navigation';
import { useDispatch, useSelector } from 'react-redux';
import StepWrapper from './stepWrapper';

export default function EmailVerification({
  stepname: stepName,
}: {
  stepname?: emailVerification['name'];
}): React.JSX.Element {
  const router = useRouter();
  const dispatch = useDispatch();
  const email = useSelector((state: RootState) => state.flowdata.variables?.email);
  const instanceId = getClientCookie('instanceId');

  // Initializing the step again sends a new code, same as the SMS step
  const resendOTP = () => {
    if (!instanceId || !stepName) return;
    sendOnbEvent({ type: 'interaction', subtype: 'resendOTP', data: { instanceId } });
    let failure: ReturnType<typeof failureDetails> | undefined;
    initializeStep<emailVerification>({ instanceId, name: stepName })
      .then((response) => {
        if (response.hardError || response.softError) {
          failure = failureDetails(response);
          throw new Error('resend failed');
        }
        dispatch(showToast({ text: 'Código reenviado', kind: 'success' }));
        dispatch(resetErrors());
      })
      .catch((e) => {
        sendOnbEvent({
          type: 'error',
          subtype: 'initStep',
          data: { stepName, instanceId, resend: true, ...(failure ?? failureDetails(undefined, e)) },
        });
        dispatch(showToast({ text: 'No pudimos reenviar el código. Intentá de nuevo', kind: 'error' }));
      });
  };

  return (
    <StepWrapper>
      <Typography variant="h1">Validemos tu email</Typography>
      <Stack>
        <Typography component="p" variant="subtitle2">
          Te mandamos un código a: <span style={{ fontWeight: 'bold' }}>{email}</span>
        </Typography>
        <Button
          variant="link"
          sx={{ mt: 1 }}
          onClick={() =>
            router.push(
              process.env.NEXT_PUBLIC_ONB_INSTANCE_TYPE === 'onb-comercios'
                ? '/flow/MERCHANT_CONTACT_INFORMATION'
                : '/flow/CONTACT_INFORMATION',
            )
          }
        >
          Cambiar email
        </Button>
      </Stack>
      <OnbForm<emailVerification> stepName={stepName!}>
        <Otp
          fields={4}
          resend={resendOTP}
          {...connectedField({
            stepName,
            name: 'emailOtp',
            required: true,
            errorHandler: {
              type: 'text',
              errorText: 'Ingresá un código válido',
              validate: (value) => value.length > 3,
            },
          })}
        />
      </OnbForm>
    </StepWrapper>
  );
}
